import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

/* ─── Constants ────────────────────────────────────────────────── */
const ORANGE = "#F27024";
const BLUE   = "#1E5AA8";

/* ─── Data ─────────────────────────────────────────────────────── */
const eligibilityPoints = [
    "10+2 (PUC/Intermediate) from a recognised board",
    "Physics & Chemistry as compulsory subjects",
    "With Mathematics (PCM), Biology (PCB), or both (PCMB)",
    "Minimum 40% aggregate in science subjects",
];

const careerOpportunities = [
    "Pharmacist", "Clinical Pharmacist", "Drug Information Specialist",
    "Drug Safety Associate", "Medical Reviewer", "Clinical Data Analyst",
    "Production Officer", "Quality Control (QC)", "Quality Assurance (QA)",
    "Regulatory Affairs", "Formulation Development Scientist",
    "Analytical Research Scientist", "Clinical Research Scientist", "Medical Writer",
];

const skillsDeveloped = [
    { icon: "fal fa-flask",    text: "Formulation, testing, and quality control of pharmaceutical products" },
    { icon: "fal fa-heartbeat",text: "Clinical pharmacology and drug interaction analysis" },
    { icon: "fal fa-file-alt", text: "Regulatory compliance and documentation" },
    { icon: "fal fa-brain",    text: "Critical thinking and scientific research methodology" },
    { icon: "fal fa-user-md",  text: "Patient counselling and communication in healthcare settings" },
    { icon: "fal fa-eye",      text: "Precision, attention to detail, and ethical professional conduct" },
    { icon: "fal fa-users",    text: "Leadership, teamwork, and problem-solving in pharmaceutical contexts" },
];

const whyChoosePoints = [
    { icon: "fal fa-university", text: "Affiliated to RGUHS, approved by Pharmacy Council of India & Govt. of Karnataka" },
    { icon: "fal fa-industry",       text: "Industry-aligned curriculum and experiential learning approach" },
    { icon: "fal fa-microscope",     text: "Advanced laboratories and research facilities" },
    { icon: "fal fa-user-tie",       text: "Expert faculty with academic and industry experience" },
    { icon: "fal fa-hands-helping",  text: "Mentorship, academic counselling, and skill-development workshops" },
    { icon: "fal fa-seedling",       text: "Strong focus on ethics, innovation, and community health impact" },
];

const heroBadges = [
    { icon: "fal fa-landmark", label: "Approved by Govt. of Karnataka" },
    { icon: "fal fa-clock",        label: "4 Years · 8 Semesters" },
    { icon: "fal fa-book",         label: "PCM / PCB / PCMB" },
    { icon: "fal fa-check-circle", label: "Pharmacy Council of India Approved" },
    { icon: "fal fa-university",   label: "RGUHS Affiliated" },
];

/* ─── Shared Variants ──────────────────────────────────────────── */
const fadeUp = {
    hidden: { opacity: 0, y: 45 },
    visible: (delay = 0) => ({
        opacity: 1, y: 0,
        transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

const fadeDown = {
    hidden: { opacity: 0, y: -35 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

const staggerGrid = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const gridItem = {
    hidden: { opacity: 0, y: 28, scale: 0.96 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
    },
};

const pillStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.06, delayChildren: 0.25 } },
};

const pillItem = {
    hidden: { opacity: 0, scale: 0.75, y: 10 },
    visible: {
        opacity: 1, scale: 1, y: 0,
        transition: { duration: 0.4, ease: [0.34, 1.56, 0.64, 1] },
    },
};

/* ─── SectionLabel ─────────────────────────────────────────────── */
function SectionLabel({ icon, text, color }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "1.5rem" }}>
            <div style={{
                width: "36px", height: "36px", borderRadius: "8px",
                background: color === ORANGE ? "#fff4ec" : "#e8f0fb",
                display: "flex", alignItems: "center", justifyContent: "center",
            }}>
                <i className={icon} style={{ color, fontSize: "16px" }} />
            </div>
            <p style={{
                fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em",
                color, textTransform: "uppercase", margin: 0,
            }}>
                {text}
            </p>
        </div>
    );
}

/* ─── Reusable animated card wrapper ──────────────────────────── */
function AnimCard({ children, style, delay = 0 }) {
    const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
    const cardRef = useRef(null);

    const setRefs = (el) => { cardRef.current = el; ref(el); };

    useEffect(() => {
        if (inView && cardRef.current) {
            gsap.fromTo(
                cardRef.current,
                { boxShadow: '0 0 0px rgba(30,90,168,0)' },
                { boxShadow: style?.boxShadow || '0 2px 12px rgba(30,90,168,0.07)', duration: 1, ease: 'power2.out', delay: delay + 0.3 }
            );
        }
    }, [inView]);

    return (
        <motion.div
            ref={setRefs}
            variants={fadeUp}
            custom={delay}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            style={{ ...style, boxShadow: 'none', marginBottom: '40px' }}
        >
            {children}
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function CourseDetail() {

    /* Hero banner */
    const { ref: heroRef, inView: heroInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: hero shimmer sweep */
    const shimmerRef = useRef(null);
    useEffect(() => {
        if (heroInView && shimmerRef.current) {
            gsap.fromTo(
                shimmerRef.current,
                { x: '-100%', opacity: 0.5 },
                { x: '200%', opacity: 0, duration: 1.1, ease: 'power2.inOut', delay: 0.6 }
            );
        }
    }, [heroInView]);

    /* GSAP: CTA button hover */
    const ctaBtnRef = useRef(null);
    const handleCtaEnter = () => gsap.to(ctaBtnRef.current, { scale: 1.06, duration: 0.3, ease: 'power2.out' });
    const handleCtaLeave = () => gsap.to(ctaBtnRef.current, { scale: 1,    duration: 0.3, ease: 'power2.out' });

    /* CTA section */
    const { ref: ctaRef, inView: ctaInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Duration + Eligibility row */
    const { ref: durationRef, inView: durationInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: big "4" number count-up */
    const bigNumRef = useRef(null);
    useEffect(() => {
        if (durationInView && bigNumRef.current) {
            gsap.fromTo(
                bigNumRef.current,
                { innerText: 0 },
                {
                    innerText: 4, duration: 1.2, ease: 'power2.out',
                    snap: { innerText: 1 },
                    onUpdate() {
                        if (bigNumRef.current)
                            bigNumRef.current.innerText = Math.round(this.targets()[0].innerText);
                    },
                }
            );
        }
    }, [durationInView]);

    /* Skills grid */
    const { ref: skillsRef, inView: skillsInView } = useInView({ threshold: 0.15, triggerOnce: true });

    /* Career pills */
    const { ref: careerRef, inView: careerInView } = useInView({ threshold: 0.15, triggerOnce: true });

    /* Why Choose grid */
    const { ref: whyRef, inView: whyInView } = useInView({ threshold: 0.15, triggerOnce: true });

    return (
        <section className="shop-area pt-120 pb-120 p-relative fix">
            <div className="container">
                <div className="row">
                    <div className="col-lg-12">

                        {/* ══ Hero Banner ══════════════════════════════════════ */}
                        <motion.div
                            ref={heroRef}
                            variants={fadeUp}
                            custom={0}
                            initial="hidden"
                            animate={heroInView ? 'visible' : 'hidden'}
                            style={{ borderRadius: "16px", overflow: "hidden", boxShadow: "0 8px 32px rgba(30,90,168,0.15)", marginBottom: "40px" }}
                        >
                            <div style={{
                                background: `linear-gradient(135deg, ${BLUE} 0%, #0d3d7a 100%)`,
                                padding: "3rem 2.5rem 2rem", position: "relative", overflow: "hidden",
                            }}>
                                {/* Decorative circles */}
                                <div style={{ position: "absolute", top: "-40px", right: "-40px", width: "200px", height: "200px", borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
                                <div style={{ position: "absolute", bottom: "-60px", right: "120px", width: "140px", height: "140px", borderRadius: "50%", background: "rgba(242,112,36,0.15)" }} />

                                {/* Shimmer sweep (GSAP) */}
                                <div ref={shimmerRef} style={{
                                    position: "absolute", top: 0, left: 0, width: "55%", height: "100%",
                                    background: "linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.2) 50%, transparent 80%)",
                                    pointerEvents: "none",
                                }} />

                                <motion.span
                                    variants={fadeDown}
                                    initial="hidden"
                                    animate={heroInView ? 'visible' : 'hidden'}
                                    style={{
                                        display: "inline-block", background: "rgba(255,255,255,0.15)", color: "#fff",
                                        fontSize: "11px", fontWeight: 600, padding: "5px 16px", borderRadius: "20px",
                                        border: "1px solid rgba(255,255,255,0.3)", marginBottom: "1.25rem", letterSpacing: "0.08em",
                                    }}
                                >
                                    Undergraduate Program
                                </motion.span>

                                <motion.h1
                                    variants={fadeUp} custom={0.1}
                                    initial="hidden" animate={heroInView ? 'visible' : 'hidden'}
                                    style={{ fontSize: "48px", fontWeight: 800, color: "#fff", margin: "0 0 8px", lineHeight: 1.1 }}
                                >
                                    B.Pharmacy
                                </motion.h1>

                                <motion.p
                                    variants={fadeUp} custom={0.2}
                                    initial="hidden" animate={heroInView ? 'visible' : 'hidden'}
                                    style={{ color: "rgba(255,255,255,0.75)", fontSize: "15px", margin: "0 0 1.75rem" }}
                                >
                                    Bachelor of Pharmacy — Akash Institute of Pharmacy
                                </motion.p>

                                {/* Hero badges — staggered */}
                                <motion.div
                                    style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}
                                    variants={pillStagger}
                                    initial="hidden"
                                    animate={heroInView ? 'visible' : 'hidden'}
                                >
                                    {heroBadges.map((item, i) => (
                                        <motion.span
                                            key={i}
                                            variants={pillItem}
                                            whileHover={{ scale: 1.07, y: -2 }}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: "6px",
                                                background: "rgba(255,255,255,0.12)", color: "#fff",
                                                fontSize: "12px", fontWeight: 500, padding: "6px 14px",
                                                borderRadius: "20px", border: "1px solid rgba(255,255,255,0.2)",
                                                cursor: "default",
                                            }}
                                        >
                                            <i className={item.icon} />{item.label}
                                        </motion.span>
                                    ))}
                                </motion.div>
                            </div>

                            {/* Orange accent bar — wipe in */}
                            <motion.div
                                style={{ height: "5px", background: `linear-gradient(90deg, ${ORANGE}, #ffb347)`, transformOrigin: "left" }}
                                initial={{ scaleX: 0 }}
                                animate={heroInView ? { scaleX: 1 } : { scaleX: 0 }}
                                transition={{ duration: 0.9, ease: "easeOut", delay: 0.5 }}
                            />
                        </motion.div>

                        {/* ══ About the Course ═════════════════════════════════ */}
                        <AnimCard
                            style={{ background: "#fff", border: "1px solid #ffe0c2", borderRadius: "16px", padding: "2rem 2.5rem" }}
                            delay={0}
                        >
                            <SectionLabel icon="fal fa-info-circle" text="About the Course" color={ORANGE} />
                            <p style={{ fontSize: "15px", lineHeight: 1.9, color: "#374151", margin: "0 0 1rem" }}>
                                The Bachelor of Pharmacy (B.Pharm) is a four-year undergraduate program designed to prepare
                                students for a professional career in pharmaceutical sciences. The course provides comprehensive
                                knowledge of drug formulation, pharmacology, medicinal chemistry, pharmacognosy, and pharmacy practice.
                            </p>
                            <p style={{ fontSize: "15px", lineHeight: 1.9, color: "#374151", margin: 0 }}>
                                At Akash Institute of Pharmacy, the program focuses on academic excellence, practical training,
                                research exposure, and industry readiness.
                            </p>
                        </AnimCard>

                        {/* ══ Duration + Eligibility ═══════════════════════════ */}
                        <div className="row g-3" style={{ marginBottom: "40px" }} ref={durationRef}>
                            {/* Duration */}
                            <motion.div
                                className="col-md-6"
                                variants={fadeUp} custom={0}
                                initial="hidden"
                                animate={durationInView ? 'visible' : 'hidden'}
                            >
                                <div style={{
                                    background: "linear-gradient(135deg, #e8f0fb 0%, #f0f6ff 100%)",
                                    border: "1px solid #b8d0f0", borderRadius: "16px",
                                    padding: "2rem 2.5rem", height: "100%",
                                    boxShadow: "0 2px 12px rgba(30,90,168,0.07)",
                                }}>
                                    <SectionLabel icon="fal fa-calendar-alt" text="Course Duration" color={BLUE} />
                                    <div style={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
                                        <span ref={bigNumRef} style={{
                                            fontSize: "72px", fontWeight: 800, color: BLUE, lineHeight: 1,
                                            textShadow: "2px 4px 12px rgba(30,90,168,0.15)",
                                        }}>0</span>
                                        <div>
                                            <p style={{ fontSize: "18px", fontWeight: 700, color: "#111827", margin: "0 0 6px" }}>Years Program</p>
                                            <p style={{ fontSize: "13px", color: "#4b5563", lineHeight: 1.7, margin: 0 }}>
                                                Structured across 8 semesters, from pharmaceutical fundamentals to
                                                specialised practice with hands-on lab training and industry exposure.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* Eligibility */}
                            <motion.div
                                className="col-md-6"
                                variants={fadeUp} custom={0.15}
                                initial="hidden"
                                animate={durationInView ? 'visible' : 'hidden'}
                            >
                                <div style={{
                                    background: "#fff", border: "1px solid #ffe0c2",
                                    borderRadius: "16px", padding: "2rem 2.5rem", height: "100%",
                                    boxShadow: "0 2px 12px rgba(242,112,36,0.07)",
                                }}>
                                    <SectionLabel icon="fal fa-clipboard-list" text="Eligibility" color={ORANGE} />
                                    <motion.ul
                                        style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "10px" }}
                                        variants={staggerGrid}
                                        initial="hidden"
                                        animate={durationInView ? 'visible' : 'hidden'}
                                    >
                                        {eligibilityPoints.map((point, i) => (
                                            <motion.li
                                                key={i}
                                                variants={gridItem}
                                                style={{ fontSize: "13px", color: "#374151", paddingLeft: "22px", position: "relative", lineHeight: 1.7 }}
                                            >
                                                <span style={{
                                                    position: "absolute", left: 0, top: "6px",
                                                    width: "8px", height: "8px",
                                                    background: `linear-gradient(135deg, ${ORANGE}, #ffb347)`,
                                                    borderRadius: "50%",
                                                }} />
                                                {point}
                                            </motion.li>
                                        ))}
                                    </motion.ul>
                                </div>
                            </motion.div>
                        </div>

                        {/* ══ Skills Developed ═════════════════════════════════ */}
                        <motion.div
                            ref={skillsRef}
                            variants={fadeUp} custom={0}
                            initial="hidden"
                            animate={skillsInView ? 'visible' : 'hidden'}
                            style={{
                                background: "#fff", border: "1px solid #b8d0f0",
                                borderRadius: "16px", padding: "2rem 2.5rem",
                                boxShadow: "0 2px 12px rgba(30,90,168,0.07)", marginBottom: "40px",
                            }}
                        >
                            <SectionLabel icon="fal fa-star" text="Skills Developed" color={BLUE} />
                            <motion.div
                                className="row g-3"
                                variants={staggerGrid}
                                initial="hidden"
                                animate={skillsInView ? 'visible' : 'hidden'}
                            >
                                {skillsDeveloped.map((skill, i) => (
                                    <motion.div
                                        key={i}
                                        className="col-lg-4 col-md-6"
                                        variants={gridItem}
                                    >
                                        <motion.div
                                            whileHover={{ scale: 1.03, y: -3 }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: "linear-gradient(135deg, #f0f6ff 0%, #e8f0fb 100%)",
                                                border: "1px solid #b8d0f0", borderRadius: "12px",
                                                padding: "1.25rem", height: "100%",
                                                display: "flex", alignItems: "center", gap: "12px",
                                            }}
                                        >
                                            <div style={{
                                                width: "34px", height: "34px", borderRadius: "8px",
                                                background: "#fff", border: "1px solid #b8d0f0",
                                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                            }}>
                                                <i className={skill.icon} style={{ color: BLUE, fontSize: "14px" }} />
                                            </div>
                                            <p style={{ fontSize: "13px", color: "#374151", lineHeight: 1.6, margin: 0 }}>
                                                {skill.text}
                                            </p>
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </motion.div>

                        {/* ══ Career Opportunities ═════════════════════════════ */}
                        <motion.div
                            ref={careerRef}
                            variants={fadeUp} custom={0}
                            initial="hidden"
                            animate={careerInView ? 'visible' : 'hidden'}
                            style={{
                                background: "#fff", border: "1px solid #b8d0f0",
                                borderRadius: "16px", padding: "2rem 2.5rem",
                                boxShadow: "0 2px 12px rgba(30,90,168,0.07)", marginBottom: "40px",
                            }}
                        >
                            <SectionLabel icon="fal fa-briefcase" text="Career Opportunities" color={BLUE} />
                            <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "1.25rem", lineHeight: 1.7 }}>
                                A career in pharmacy offers a unique blend of science, healthcare, and innovation.
                                Graduates from AIP find fulfilling roles across pharmaceutical research, manufacturing,
                                clinical research, regulatory affairs, and academia.
                            </p>
                            <motion.div
                                style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}
                                variants={pillStagger}
                                initial="hidden"
                                animate={careerInView ? 'visible' : 'hidden'}
                            >
                                {careerOpportunities.map((career, i) => (
                                    <motion.span
                                        key={i}
                                        variants={pillItem}
                                        whileHover={{ scale: 1.08, y: -2 }}
                                        style={{
                                            fontSize: "13px", fontWeight: 500, color: ORANGE,
                                            background: "#fff4ec", border: "1px solid #ffd4b0",
                                            padding: "8px 18px", borderRadius: "20px", cursor: "default",
                                        }}
                                    >
                                        {career}
                                    </motion.span>
                                ))}
                            </motion.div>
                        </motion.div>

                        {/* ══ Why Choose AIP ══════════════════════════════════ */}
                        <motion.div
                            ref={whyRef}
                            variants={fadeUp} custom={0}
                            initial="hidden"
                            animate={whyInView ? 'visible' : 'hidden'}
                            style={{
                                background: "#fff", border: "1px solid #ffe0c2",
                                borderRadius: "16px", padding: "2rem 2.5rem",
                                boxShadow: "0 2px 12px rgba(242,112,36,0.07)", marginBottom: "40px",
                            }}
                        >
                            <SectionLabel icon="fal fa-trophy" text="Why Choose Akash Institute of Pharmacy" color={ORANGE} />
                            <motion.div
                                className="row g-3"
                                variants={staggerGrid}
                                initial="hidden"
                                animate={whyInView ? 'visible' : 'hidden'}
                            >
                                {whyChoosePoints.map((point, i) => (
                                    <motion.div
                                        key={i}
                                        className="col-lg-4 col-md-6"
                                        variants={gridItem}
                                    >
                                        <motion.div
                                            whileHover={{ scale: 1.03, y: -3 }}
                                            transition={{ duration: 0.25 }}
                                            style={{
                                                background: "linear-gradient(135deg, #fff8f3 0%, #fff4ec 100%)",
                                                border: "1px solid #ffd4b0", borderRadius: "12px",
                                                padding: "1.25rem", height: "100%",
                                                display: "flex", alignItems: "flex-start", gap: "12px",
                                            }}
                                        >
                                            <div style={{
                                                width: "34px", height: "34px", borderRadius: "8px",
                                                background: "#fff", border: "1px solid #ffd4b0",
                                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                            }}>
                                                <i className={point.icon} style={{ color: ORANGE, fontSize: "14px" }} />
                                            </div>
                                            <p style={{ fontSize: "13px", color: "#374151", lineHeight: 1.6, margin: 0 }}>
                                                {point.text}
                                            </p>
                                        </motion.div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        </motion.div>

                        {/* ══ CTA Banner ═══════════════════════════════════════ */}
                        <motion.div
                            ref={ctaRef}
                            variants={fadeUp} custom={0}
                            initial="hidden"
                            animate={ctaInView ? 'visible' : 'hidden'}
                            style={{
                                background: `linear-gradient(135deg, ${ORANGE} 0%, #d45e10 100%)`,
                                borderRadius: "16px", padding: "2.5rem",
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                flexWrap: "wrap", gap: "1.5rem",
                                boxShadow: "0 8px 32px rgba(242,112,36,0.3)",
                                position: "relative", overflow: "hidden",
                            }}
                        >
                            <div style={{
                                position: "absolute", top: "-30px", right: "160px",
                                width: "120px", height: "120px", borderRadius: "50%",
                                background: "rgba(255,255,255,0.08)",
                            }} />

                            <motion.div
                                variants={fadeUp} custom={0.1}
                                initial="hidden"
                                animate={ctaInView ? 'visible' : 'hidden'}
                            >
                                <h3 style={{ fontSize: "24px", fontWeight: 700, color: "#fff", margin: "0 0 6px" }}>
                                    Ready to begin your pharmacy journey?
                                </h3>
                                <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", margin: 0 }}>
                                    Apply now for the B.Pharmacy program at Akash Institute of Pharmacy.
                                </p>
                            </motion.div>

                            <motion.div
                                ref={ctaBtnRef}
                                variants={fadeUp} custom={0.25}
                                initial="hidden"
                                animate={ctaInView ? 'visible' : 'hidden'}
                                style={{ display: "inline-block" }}
                                onMouseEnter={handleCtaEnter}
                                onMouseLeave={handleCtaLeave}
                            >
                                <Link to="/contact-us" style={{
                                    background: "#fff", color: ORANGE, fontWeight: 700, fontSize: "14px",
                                    padding: "14px 32px", borderRadius: "30px", textDecoration: "none",
                                    whiteSpace: "nowrap", display: "inline-block",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                }}>
                                    Apply Now →
                                </Link>
                            </motion.div>
                        </motion.div>

                    </div>
                </div>
            </div>
        </section>
    );
}

export default CourseDetail;
